import { observable } from "@legendapp/state";
import { getGeoPerformanceReport } from "@/apis/clients/reportingClient";
import {
  createRequestGuard,
  reportScopeKey,
  toErrorMessage,
} from "@/stores/storeHelpers";
import type { PerformanceReportRow, ReportScope } from "@/types/reporting";

interface GeoState {
  rows: PerformanceReportRow[];
  isLoading: boolean;
  error: string | null;
  loadedForScopeKey: string | null;
}

const defaultValues: GeoState = {
  rows: [],
  isLoading: false,
  error: null,
  loadedForScopeKey: null,
};

export const GeoStore$ = observable(defaultValues);

const getScopeKey = (scope: ReportScope) => `geo:${reportScopeKey(scope)}`;

const guard = createRequestGuard();

export const fetchGeoReport = async (scope: ReportScope) => {
  const scopeKey = getScopeKey(scope);
  const result = guard.check(scopeKey, GeoStore$.loadedForScopeKey.get());
  if (result.outcome === "cached") {
    GeoStore$.assign({ error: null, isLoading: false });
    return;
  }
  if (result.outcome === "in-flight") return;
  const { requestId } = result;
  GeoStore$.assign({ isLoading: true, error: null });

  try {
    const response = await getGeoPerformanceReport(scope);
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    GeoStore$.assign({
      rows: response.results,
      loadedForScopeKey: scopeKey,
    });
  } catch (error) {
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    GeoStore$.error.set(toErrorMessage(error));
  } finally {
    if (!guard.isStale(requestId)) {
      GeoStore$.isLoading.set(false);
    }
  }
};

export const clearGeoReport = () => {
  guard.invalidate();
  GeoStore$.assign({
    rows: [],
    isLoading: false,
    loadedForScopeKey: null,
    error: null,
  });
};
